import React, { useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Upload, X, Video } from "lucide-react"
import { useToast } from "@/hooks/use-toast"

interface VideoUploadProps {
    value?: File | string | null
    onChange: (file: File | null) => void
    label?: string
    className?: string
    maxSizeMB?: number
}

export function VideoUpload({
    value,
    onChange,
    label = "Загрузить видео",
    className,
    maxSizeMB = 100
}: VideoUploadProps) {
    const { toast } = useToast()
    const inputRef = useRef<HTMLInputElement>(null)
    const [fileName, setFileName] = useState<string | null>(
        value instanceof File ? value.name : typeof value === "string" ? value.split('/').pop() || null : null
    )
    const [fileSize, setFileSize] = useState<number | null>(
        value instanceof File ? value.size : null
    )

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            // Validate file type
            const validTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska']
            if (!validTypes.includes(file.type)) {
                toast({
                    title: "Ошибка",
                    description: "Пожалуйста, загрузите видео файл (MP4, WebM, OGG, MOV, AVI, MKV)",
                    variant: "destructive",
                })
                return
            }

            // Validate file size (maxSizeMB in MB)
            const maxSizeBytes = maxSizeMB * 1024 * 1024
            if (file.size > maxSizeBytes) {
                toast({
                    title: "Ошибка",
                    description: `Размер файла не должен превышать ${maxSizeMB}MB`,
                    variant: "destructive",
                })
                return
            }

            setFileName(file.name)
            setFileSize(file.size)
            onChange(file)
        }
    }

    const handleRemove = () => {
        setFileName(null)
        setFileSize(null)
        onChange(null)
        if (inputRef.current) {
            inputRef.current.value = ""
        }
    }

    // Update state when value prop changes externally
    React.useEffect(() => {
        if (value instanceof File) {
            setFileName(value.name)
            setFileSize(value.size)
        } else if (typeof value === "string") {
            setFileName(value.split('/').pop() || null)
            setFileSize(null)
        } else if (value === null) {
            setFileName(null)
            setFileSize(null)
        }
    }, [value])

    return (
        <div className={className}>
            {fileName ? (
                <div className="flex items-center gap-3 p-4 rounded-lg border bg-muted">
                    <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-primary/10">
                        <Video className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{fileName}</p>
                        {fileSize && (
                            <p className="text-xs text-muted-foreground">
                                {formatFileSize(fileSize)}
                            </p>
                        )}
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive/90"
                        onClick={handleRemove}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            ) : (
                <div className="flex items-center gap-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => inputRef.current?.click()}
                    >
                        <Upload className="mr-2 h-4 w-4" />
                        {label}
                    </Button>
                    <Input
                        ref={inputRef}
                        type="file"
                        accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo,video/x-matroska,.mp4,.webm,.ogg,.mov,.avi,.mkv"
                        className="hidden"
                        onChange={handleFileChange}
                    />
                    <span className="text-xs text-muted-foreground">
                        MP4, WebM, OGG, MOV, AVI, MKV (макс. {maxSizeMB}MB)
                    </span>
                </div>
            )}
        </div>
    )
}
